import useSWR, { SWRConfiguration } from 'swr';
import denApi from '@/lib/api/denApi';
import type { PolicyProposalImpactPreview } from '@mls-wallet/shared-types';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';

const fetcher = async (url: string) =>
  await denApi.get<PolicyProposalImpactPreview, void>(url);

/**
 * Hook to preview the impact of an existing proposal before executing it.
 * Shows which pending transactions would be invalidated.
 *
 * @example
 * const { data, isLoading } = usePreviewPolicyProposalImpactExisting('proposal-123');
 */
export function usePreviewPolicyProposalImpactExisting(
  proposalId: string | null,
  options?: SWRConfiguration
) {
  const { organization } = useMyOrganization();
  const url =
    organization?.isAdmin && proposalId
      ? `admin/policy/proposals/${encodeURIComponent(proposalId)}/preview-impact`
      : null;

  const { data, error, isLoading, mutate } =
    useSWR<PolicyProposalImpactPreview>(url, fetcher, {
      revalidateOnFocus: false,
      ...(options ?? {}),
    });

  return {
    data,
    isLoading,
    isError: error,
    mutate,
  };
}
